import { useCallback, useEffect, useRef } from 'react';
import { useFormContext } from 'react-hook-form';
import { ButtonVariants } from '#sign-up/components/common/Button/buttonTypes';
import { IconButton } from '#sign-up/components/common/Button/IconButton';
import FormTextField from '#sign-up/components/common/FormTextField/FormTextField';
import { SignupFormValues } from '#sign-up/components/SignUp/types';
import {
  patternRule,
  PREVIOUSLY_ENTERED,
  requiredText,
  trimString,
  validateCreditCardNumber,
} from '#/Scripts/shared/form/validation';

export const CreditCardForm = () => {
  const form = useFormContext<SignupFormValues>();
  const creditCardNumber = form.watch('CreditCardNumber');
  const creditCardNumberRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    creditCardNumberRef.current ??= document.querySelector<HTMLInputElement>('input[name="CreditCardNumber"]');
  }, []);

  const handleEditCreditCardNumber = useCallback(() => {
    form.setValue('CreditCardNumber', '');
    creditCardNumberRef.current?.focus();
  }, [form]);

  const handleReadOnlyClick = useCallback((e: React.MouseEvent<HTMLInputElement>) => {
    e.preventDefault();
    (e.target as HTMLInputElement).blur();
  }, []);

  const handleReadOnlyFocus = useCallback((e: React.FocusEvent<HTMLInputElement>) => {
    e.preventDefault();
    e.target.blur();
  }, []);

  const renderEditIcon = useCallback(
    (isReadonly: boolean, onClick: () => void, testId: string) =>
      isReadonly ? (
        <IconButton
          className="edit-icon"
          iconName="edit"
          title="Edit"
          onClick={onClick}
          variant={ButtonVariants.outline}
          data-testid={testId}
          style={{ border: 'none', boxShadow: 'none' }}
        />
      ) : undefined,
    []
  );

  return (
    <div>
      <div className="signup-form-header">
        <h1 className="signup-form-section-header">Subscription Account Information</h1>
        <p>This card will be used for your monthly subscription fee.</p>
      </div>
      <div className="signup-form-row">
        <FormTextField
          label="Cardholder Name"
          className="with-subtext"
          {...form.register('CreditCardName', {
            ...requiredText('Cardholder Name'),
            ...trimString,
          })}
          errors={form.formState.errors.CreditCardName}
        />
        <FormTextField
          label="Credit Card Number"
          className="with-subtext"
          placeholder={creditCardNumber === PREVIOUSLY_ENTERED ? 'Previously entered' : undefined}
          readOnly={creditCardNumber === PREVIOUSLY_ENTERED}
          onClick={creditCardNumber === PREVIOUSLY_ENTERED ? handleReadOnlyClick : undefined}
          onFocus={creditCardNumber === PREVIOUSLY_ENTERED ? handleReadOnlyFocus : undefined}
          icon={renderEditIcon(
            creditCardNumber === PREVIOUSLY_ENTERED,
            handleEditCreditCardNumber,
            'edit-credit-card-number'
          )}
          maxLength={16}
          {...form.register('CreditCardNumber', {
            validate: validateCreditCardNumber,
            ...trimString,
          })}
          errors={form.formState.errors.CreditCardNumber}
        />
      </div>
      <div className="signup-form-row">
        <FormTextField
          label="Expiration Month"
          className="with-subtext"
          placeholder="MM"
          maxLength={2}
          {...form.register('CreditCardMonth', {
            ...requiredText('Expiration Month'),
            ...patternRule(/^(0[1-9]|1[0-2])$/, 'Month must be a valid 2-digit month (01-12).'),
            ...trimString,
          })}
          errors={form.formState.errors.CreditCardMonth}
        />
        <FormTextField
          label="Expiration Year"
          className="with-subtext"
          placeholder="YYYY"
          maxLength={4}
          {...form.register('CreditCardYear', {
            ...requiredText('Expiration Year'),
            ...patternRule(/^\d{4}$/, 'Year must be 4 digits.'),
            ...trimString,
          })}
          errors={form.formState.errors.CreditCardYear}
        />
        <FormTextField
          label="CVC"
          className="with-subtext"
          maxLength={4}
          {...form.register('CreditCardCvc', {
            ...requiredText('CVC'),
            ...patternRule(/^\d{3,4}$/, 'CVC must be 3 or 4 digits.'),
            ...trimString,
          })}
          errors={form.formState.errors.CreditCardCvc}
        />
        <FormTextField
          label="Postal Code"
          className="with-subtext"
          maxLength={7}
          {...form.register('CreditCardPostalCode', {
            ...requiredText('Postal Code'),
            ...trimString,
          })}
          errors={form.formState.errors.CreditCardPostalCode}
        />
      </div>
    </div>
  );
};
